// Pointer-interaction logic for the dock tab — disambiguates a tap
// (toggle the panel) from a drag (reposition the dock). Extracted from
// `ActivityFloatingDock` so the dock file stays under the file-size
// hygiene caps as new workflow-facilitation behavior (peek lifecycle,
// assist-level wiring) lands.
//
// Concept:
//   - `handlePointerDown` captures the pointer and snapshots the down
//     position so subsequent moves can compute a delta.
//   - `handlePointerMove` flips into drag mode once the delta passes
//     `DRAG_THRESHOLD_PX` on either axis; up until then it's a no-op so
//     a stationary tap doesn't accidentally reposition the dock.
//   - `handlePointerUp` either ends the drag (snap to nearest edge) or,
//     if `moved` never flipped, toggles the panel open/closed.
//   - `handlePointerCancel` resets state without committing either
//     action.
//
// Dev-sync 2026-06-18: release pointer capture on cancel, blur, and
// visibility hidden so the firefly tab cannot stay glued to the cursor
// after a browser tab switch mid-drag.

import { useCallback, useEffect, useRef } from "react";
import type { Dispatch, PointerEvent as ReactPointerEvent, SetStateAction } from "react";
import { ACTIVITY_DOCK_DRAG_THRESHOLD_PX } from "./activityDockPointerConstants";
import type { ActivityDockPttGestureBridge } from "./useActivityDockPttGesture";
import { classifyFireflyTabRelease } from "@/lib/firefly/fireflyTabTap";
import { FIREFLY_DOUBLE_CLICK_MS } from "@/lib/firefly/fireflyPttConstants";

const DRAG_THRESHOLD_PX = ACTIVITY_DOCK_DRAG_THRESHOLD_PX;

type CaptureState = {
  target: HTMLElement;
  pointerId: number;
};

type DownState = {
  x: number;
  y: number;
  moved: boolean;
  /** Pointer − element-center at drag arm; keeps the bug from teleporting. */
  grabOffsetX: number;
  grabOffsetY: number;
};

export type UseActivityDockTapVsDragArgs = {
  /** Current open state (kept for callers; open panel hides the Firefly tab). */
  open: boolean;
  /** Standard `useState` setter — accepts both a value and an updater. */
  setOpen: Dispatch<SetStateAction<boolean>>;
  beginDrag: (clientX: number, clientY: number) => void;
  updateDrag: (clientX: number, clientY: number) => void;
  endDrag: (clientX: number, clientY: number, viewportW: number, viewportH: number) => void;
  cancelDrag: () => void;
  /** Optional long-press PTT overlay (§3.1 / §7.1). */
  pttBridge?: ActivityDockPttGestureBridge;
  /** Desktop: a second tap inside the double-click window starts PTT. */
  onDoubleClickPtt?: () => void;
};

export type ActivityDockPointerHandlers = {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
};

export function releaseActivityDockPointerCapture(
  capture: CaptureState | null,
): void {
  if (!capture) return;
  try {
    if (capture.target.hasPointerCapture(capture.pointerId)) {
      capture.target.releasePointerCapture(capture.pointerId);
    }
  } catch {
    // Target may be detached after navigation.
  }
}

export function useActivityDockTapVsDrag(
  args: UseActivityDockTapVsDragArgs,
): ActivityDockPointerHandlers {
  const { setOpen, beginDrag, updateDrag, endDrag, cancelDrag, pttBridge, onDoubleClickPtt } = args;

  const downRef = useRef<DownState | null>(null);
  const captureRef = useRef<CaptureState | null>(null);
  const lastTapMsRef = useRef<number | null>(null);
  const pendingToggleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDoubleClickPttRef = useRef(onDoubleClickPtt);
  onDoubleClickPttRef.current = onDoubleClickPtt;

  const clearPendingToggle = useCallback(() => {
    if (pendingToggleRef.current == null) return;
    clearTimeout(pendingToggleRef.current);
    pendingToggleRef.current = null;
  }, []);

  const releaseCapture = useCallback(() => {
    releaseActivityDockPointerCapture(captureRef.current);
    captureRef.current = null;
  }, []);

  const abortInteraction = useCallback(() => {
    const wasDragging = downRef.current?.moved ?? false;
    releaseCapture();
    clearPendingToggle();
    pttBridge?.onPointerCancel();
    downRef.current = null;
    if (wasDragging) {
      cancelDrag();
    }
  }, [cancelDrag, clearPendingToggle, pttBridge, releaseCapture]);

  useEffect(() => clearPendingToggle, [clearPendingToggle]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        abortInteraction();
      }
    };

    window.addEventListener("blur", abortInteraction);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("blur", abortInteraction);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [abortInteraction]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      captureRef.current = { target, pointerId: e.pointerId };
      const rect = target.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      downRef.current = {
        x: e.clientX,
        y: e.clientY,
        moved: false,
        grabOffsetX: e.clientX - centerX,
        grabOffsetY: e.clientY - centerY,
      };
      pttBridge?.onPointerDown(e.pointerType);
    },
    [pttBridge],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      const down = downRef.current;
      if (!down) return;
      pttBridge?.onPointerMove(down, e.clientX, e.clientY);
      const dx = Math.abs(e.clientX - down.x);
      const dy = Math.abs(e.clientY - down.y);
      if (!down.moved) {
        if (dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return;
        down.moved = true;
        // Keep panel open while dragging — Firefly tab hides when open instead.
        beginDrag(e.clientX - down.grabOffsetX, e.clientY - down.grabOffsetY);
      } else {
        updateDrag(e.clientX - down.grabOffsetX, e.clientY - down.grabOffsetY);
      }
    },
    [beginDrag, pttBridge, updateDrag],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      releaseCapture();
      const down = downRef.current;
      downRef.current = null;
      if (!down) return;
      const pttConsumed = pttBridge?.onPointerUp() ?? false;
      if (down.moved) {
        clearPendingToggle();
        endDrag(
          e.clientX - down.grabOffsetX,
          e.clientY - down.grabOffsetY,
          window.innerWidth,
          window.innerHeight,
        );
      } else if (!pttConsumed) {
        const nowMs = Date.now();
        const release = classifyFireflyTabRelease({
          pointerType: e.pointerType,
          nowMs,
          previousTapMs: lastTapMsRef.current,
          armsDoubleClickPtt: Boolean(onDoubleClickPttRef.current),
        });
        if (release === "double_click_ptt") {
          clearPendingToggle();
          lastTapMsRef.current = null;
          onDoubleClickPttRef.current?.();
        } else if (release === "defer") {
          lastTapMsRef.current = nowMs;
          clearPendingToggle();
          pendingToggleRef.current = setTimeout(() => {
            pendingToggleRef.current = null;
            lastTapMsRef.current = null;
            setOpen((prev) => !prev);
          }, FIREFLY_DOUBLE_CLICK_MS);
        } else {
          clearPendingToggle();
          lastTapMsRef.current = null;
          setOpen((prev) => !prev);
        }
      }
    },
    [clearPendingToggle, endDrag, pttBridge, releaseCapture, setOpen],
  );

  const onPointerCancel = useCallback(() => {
      releaseCapture();
      const down = downRef.current;
      downRef.current = null;
      pttBridge?.onPointerCancel();
      if (down?.moved) {
        cancelDrag();
      }
  }, [cancelDrag, pttBridge, releaseCapture]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
